import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import HeroBackground from "@/components/site/HeroBackground";

const BOTTLES = [
  { id: "terra-arsa", img: "/img/terra-arsa-cut.png", name: "Terra Arsa", sub: "Nero d'Avola · DOC", color: "#63171B", aura: "rgba(138,74,43,0.45)" },
  { id: "scilla", img: "/img/scilla-cut.png", name: "Scilla", sub: "Inzolia · I.G.T.", color: "#0A0A0A", aura: "rgba(74,127,155,0.45)" },
  { id: "blanche", img: "/img/blanche-cut.png", name: "Blanche", sub: "Blanc de Noirs · 1.333", color: "#8a6d3b", aura: "rgba(217,185,106,0.5)" },
  { id: "claire", img: "/img/claire-cut.png", name: "Claire", sub: "Rosato · Micro-lotto", color: "#B76E79", aura: "rgba(232,180,192,0.55)" },
];

function ShowBottle({ b, i, progress }) {
  const idx = useTransform(progress, [0.04, 0.96], [0, 3], { clamp: true });
  const offset = useTransform(idx, (v) => i - v);
  const x = useTransform(offset, [-3, -1, 0, 1, 3], [-820, -400, 0, 400, 820]);
  const scale = useTransform(offset, [-2, -1, 0, 1, 2], [0.5, 0.62, 1.12, 0.62, 0.5]);
  const opacity = useTransform(offset, [-2.2, -1, 0, 1, 2.2], [0, 0.35, 1, 0.35, 0]);
  const rotateY = useTransform(offset, [-1.5, 0, 1.5], [42, 0, -42]);
  const auraOp = useTransform(offset, [-0.6, 0, 0.6], [0, 1, 0]);
  const z = useTransform(offset, (v) => 20 - Math.round(Math.abs(v) * 5));
  return (
    <motion.div
      style={{ x, scale, opacity, rotateY, zIndex: z }}
      className="absolute inset-0 flex flex-col items-center justify-center will-change-transform"
      data-testid={`hero-bottle-${b.id}`}
    >
      <motion.div
        aria-hidden
        style={{
          opacity: auraOp,
          background: `radial-gradient(ellipse at center, ${b.aura} 0%, transparent 68%)`,
        }}
        className="absolute w-[70vw] max-w-[560px] h-[68%] rounded-full blur-3xl"
      />
      <img
        src={b.img}
        alt={`${b.name} — ${b.sub}`}
        draggable={false}
        className="relative h-[44vh] sm:h-[54vh] lg:h-[60vh] w-auto object-contain drop-shadow-2xl pointer-events-none"
      />
      <motion.div style={{ opacity: auraOp }} className="relative text-center mt-6">
        <p className="font-serif-display italic text-2xl sm:text-3xl" style={{ color: b.color }}>
          {b.name}
        </p>
        <p className="overline mt-1.5 text-[9px]">{b.sub}</p>
      </motion.div>
    </motion.div>
  );
}

function ShowcaseDot({ i, progress }) {
  const idx = useTransform(progress, [0.04, 0.96], [0, 3], { clamp: true });
  const op = useTransform(idx, (v) => (Math.abs(v - i) < 0.5 ? 1 : 0.25));
  return <motion.span style={{ opacity: op }} className="w-8 h-[2px] bg-[#C4A671]" />;
}

function BottleShowcase() {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });

  return (
    <div ref={wrapRef} className="relative h-[420vh]" data-testid="hero-showcase">
      <div
        className="sticky top-0 h-screen flex items-center justify-center overflow-hidden"
        style={{ perspective: 1400 }}
      >
        <div className="relative w-full h-full">
          {BOTTLES.map((b, i) => (
            <ShowBottle key={b.id} b={b} i={i} progress={scrollYProgress} />
          ))}
        </div>
        <div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-3"
          data-testid="showcase-dots"
        >
          {BOTTLES.map((b, i) => (
            <ShowcaseDot key={b.id} i={i} progress={scrollYProgress} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full"
      style={{ background: "#FDFBF7" }}
    >
      <HeroBackground />

      {/* Vertical rail label */}
      <div className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-10">
        <span className="v-label">Sicilia Sud-Orientale · Anno MMXXV</span>
      </div>
      <div className="hidden lg:flex absolute right-6 top-1/2 -translate-y-1/2 z-10">
        <span className="v-label">Allocazioni Limitate · Ho.Re.Ca · Export</span>
      </div>

      <div className="relative z-10 max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 pt-36 sm:pt-40 pb-16">
        {/* Top metadata row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex items-center justify-between mb-10 sm:mb-16"
        >
          <span className="overline text-[10px]">Capitolo 01 — Selezione d'Autore</span>
          <span className="overline text-[10px] hidden sm:inline">Vintage 2025</span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
          className="font-serif-display tracking-tight text-[#0A0A0A] leading-[0.92]"
          style={{ fontSize: "clamp(3.2rem, 11vw, 11rem)" }}
        >
          La Valle
          <br />
          <span className="italic font-light text-[#63171B]">degli</span>{" "}
          <span className="font-light">Iblei</span>
        </motion.h1>

        {/* Sub-hero: le due linee ufficiali */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35 }}
          className="mt-8 sm:mt-10 flex flex-col gap-2.5"
          data-testid="hero-lines"
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="overline text-[9px] text-[#63171B]">Linea Territorio</span>
            <span className="text-[#C4A671]" aria-hidden>—</span>
            <button
              data-testid="hero-link-terra-arsa"
              onClick={() => document.getElementById("terra-arsa")?.scrollIntoView({ behavior: "smooth" })}
              className="font-serif-display italic text-base sm:text-lg text-[#0A0A0A] hover:text-[#63171B] transition-colors"
            >
              Terra Arsa <span className="not-italic font-sans text-[11px] text-[#7A7568]">(Nero d'Avola DOC)</span>
            </button>
            <span className="text-[#C4A671]" aria-hidden>·</span>
            <button
              data-testid="hero-link-scilla"
              onClick={() => document.getElementById("scilla")?.scrollIntoView({ behavior: "smooth" })}
              className="font-serif-display italic text-base sm:text-lg text-[#0A0A0A] hover:text-[#63171B] transition-colors"
            >
              Scilla <span className="not-italic font-sans text-[11px] text-[#7A7568]">(Inzolia IGT)</span>
            </button>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="overline text-[9px]" style={{ color: "#C4A671" }}>Linea Limited Edition</span>
            <span className="text-[#C4A671]" aria-hidden>—</span>
            <button
              data-testid="hero-link-blanche"
              onClick={() => document.getElementById("blanche")?.scrollIntoView({ behavior: "smooth" })}
              className="font-serif-display italic text-base sm:text-lg text-[#0A0A0A] hover:text-[#8a6d3b] transition-colors"
            >
              Blanche <span className="not-italic font-sans text-[11px] text-[#7A7568]">(Blanc de Noirs 1.333)</span>
            </button>
            <span className="text-[#C4A671]" aria-hidden>·</span>
            <button
              data-testid="hero-link-claire"
              onClick={() => document.getElementById("claire")?.scrollIntoView({ behavior: "smooth" })}
              className="font-serif-display italic text-base sm:text-lg text-[#0A0A0A] hover:text-[#B76E79] transition-colors"
            >
              Claire <span className="not-italic font-sans text-[11px] text-[#7A7568]">(Rosato Micro-lotto)</span>
            </button>
          </div>
        </motion.div>

        {/* Subtitle + Description grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start"
        >
          <div className="md:col-span-5">
            <p className="font-serif-display italic text-2xl sm:text-3xl leading-snug text-[#0A0A0A]">
              Selezione d'Autore per l'Alta Ristorazione e l'Export.
            </p>
          </div>
          <div className="md:col-span-1 hidden md:flex justify-center">
            <div className="w-px h-24 bg-[#C4A671]/60" />
          </div>
          <div className="md:col-span-5">
            <p className="text-base text-[#3D3D3D] leading-[1.85] font-light max-w-md">
              Un progetto enologico d'eccellenza contemporaneo, nato per portare nel
              calice l'essenza più pura e intransigente della Sicilia
              sud-orientale.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <button
                data-testid="hero-cta-allocazioni"
                onClick={() =>
                  document
                    .getElementById("contatti")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="btn-ink"
              >
                Richiedi Allocazioni
                <span aria-hidden>→</span>
              </button>
            </div>
          </div>
          <div className="md:col-span-1" />
        </motion.div>

        {/* Bottles interactive showcase */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.7, ease: "easeOut" }}
          className="relative mt-16 sm:mt-24 lg:mt-28"
        >
          <BottleShowcase />
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2"
      >
        <span className="overline text-[9px]">Scorri</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="w-px h-8 bg-[#0A0A0A]"
        />
      </motion.div>
    </section>
  );
}
